"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { authClient } from "../../lib/auth/client";
import {
  checkSize,
  describeBytes,
  MAX_PDF_BYTES,
} from "../../lib/pdf/validateFile";
import { chooseTitle, titleFromFileName } from "../../lib/reading/fileTitle";
import { newId } from "../../lib/reading/newId";
import { localReadingStore } from "../../lib/storage";
import type { PageText } from "../../lib/storage/types";
import AuthDialog from "./AuthDialog";

type State =
  | { kind: "idle" }
  | { kind: "reading"; name: string }
  | {
      kind: "naming";
      file: File;
      text: string;
      pages: PageText[];
      name: string;
    }
  | { kind: "error"; message: string }
  | { kind: "saved"; id: string; name: string };

/**
 * The front door: drop the PDF you were assigned, right on the landing page.
 *
 * The file is read in the browser and saved on the device first, so nothing
 * is lost if the student has to sign in. Signed in already, they go straight
 * on. Otherwise a dialog asks them to make an account, and wherever that
 * sign-in finishes (a password, Google, a passkey, a link in an email) it
 * lands on /start, which moves the saved reading into the account and starts
 * the rebuild. Their first reading is free either way.
 */
export default function HeroDrop({
  googleEnabled,
  mailEnabled,
}: {
  googleEnabled: boolean;
  mailEnabled: boolean;
}) {
  const router = useRouter();
  const { data: session } = authClient.useSession();
  const [state, setState] = useState<State>({ kind: "idle" });
  const [dragging, setDragging] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  async function accept(file: File) {
    const size = checkSize(file.size);
    if (!size.ok) return setState({ kind: "error", message: size.error });

    setState({ kind: "reading", name: file.name });
    // Loaded now, not with the page: pdf.js is large, and most visitors
    // only look.
    const { extractTextFromPdf } = await import("../../lib/pdf/extractText");
    const result = await extractTextFromPdf(file);
    if (!result.ok) return setState({ kind: "error", message: result.error });

    // Nothing is saved yet: the student names it first.
    setState({
      kind: "naming",
      file,
      text: result.text.trim(),
      pages: result.pages,
      name: titleFromFileName(file.name),
    });
  }

  async function saveNamed(draft: Extract<State, { kind: "naming" }>) {
    const title = chooseTitle(draft.name, titleFromFileName(draft.file.name));
    const reading = {
      id: newId(),
      title,
      sourceText: draft.text,
      createdAt: Date.now(),
      pdfBlob: draft.file,
      pages: draft.pages,
    };
    try {
      await localReadingStore.addReading(reading);
    } catch {
      return setState({
        kind: "error",
        message: "Couldn't save that on this device. Please try again.",
      });
    }

    setState({ kind: "saved", id: reading.id, name: title });
    if (session?.user) router.push(`/start?pending=${reading.id}`);
    else setDialogOpen(true);
  }

  const busy = state.kind === "reading";

  return (
    <div className="flex flex-col gap-5">
      {state.kind === "naming" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void saveNamed(state);
          }}
          className="rise-in flex min-h-[420px] flex-col items-center justify-center gap-6 rounded-[2rem] bg-ink-alt px-8 py-14 text-center sm:min-h-[480px]"
        >
          <span
            aria-hidden
            className="flex h-20 w-20 items-center justify-center rounded-full bg-blue font-heading text-4xl text-blue-deep"
          >
            ✓
          </span>
          <p className="font-heading text-2xl leading-tight text-paper sm:text-3xl">
            Got it. What should we call it?
          </p>
          <p className="font-body text-base text-paper/85">
            {state.file.name}, {state.pages.length}{" "}
            {state.pages.length === 1 ? "page" : "pages"}
          </p>
          <label className="flex w-full max-w-md flex-col gap-2 text-left">
            <span className="font-body text-base font-semibold text-paper">
              Name
            </span>
            <input
              type="text"
              value={state.name}
              maxLength={200}
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setState({ ...state, name: e.target.value })}
              className="border-2 border-transparent bg-ink px-5 py-3.5 font-body text-lg text-paper focus:border-blue"
            />
          </label>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              type="submit"
              className="bg-blue px-8 py-4 font-body text-lg font-semibold text-blue-deep hover:-translate-y-0.5 hover:opacity-90"
            >
              Continue
            </button>
            <button
              type="button"
              onClick={() => setState({ kind: "idle" })}
              className="px-4 py-3 font-body text-base text-muted-dark underline underline-offset-4 hover:text-paper"
            >
              Choose a different file
            </button>
          </div>
        </form>
      ) : (
        <label
          htmlFor="hero-pdf"
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file && !busy) void accept(file);
          }}
          className={`group flex min-h-[420px] cursor-pointer flex-col items-center justify-center gap-5 rounded-[2rem] border-2 border-dashed px-8 py-14 text-center transition-colors sm:min-h-[480px] ${
            dragging
              ? "border-blue bg-blue/10"
              : "border-paper/25 bg-ink-alt hover:border-blue"
          } ${busy ? "cursor-wait" : ""}`}
        >
          <span
            aria-hidden
            className={`flex h-20 w-20 items-center justify-center rounded-full bg-blue font-heading text-4xl text-blue-deep transition-transform duration-300 ${
              dragging ? "-translate-y-3" : "group-hover:-translate-y-1.5"
            } ${busy ? "animate-processing-pulse" : ""}`}
          >
            ↑
          </span>
          <span
            role="status"
            className="font-heading text-2xl leading-tight text-paper sm:text-3xl"
          >
            {state.kind === "reading"
              ? `Reading ${state.name}`
              : state.kind === "saved"
                ? `Got ${state.name}`
                : dragging
                  ? "Drop it here"
                  : "Drop your reading here"}
          </span>
          <span className="max-w-xs font-body text-base leading-relaxed text-paper/85">
            {state.kind === "saved"
              ? session?.user
                ? "Starting your rebuild."
                : "One quick step to start it, and it is free."
              : `Or choose a file. PDFs up to ${describeBytes(MAX_PDF_BYTES)}. Your first reading is free.`}
          </span>
          <span className="mt-2 inline-flex rounded-full bg-blue px-7 py-3.5 font-body text-base font-semibold text-blue-deep group-hover:opacity-90">
            Choose a PDF
          </span>
          <input
            id="hero-pdf"
            type="file"
            aria-label="Upload a PDF"
            accept="application/pdf,.pdf"
            disabled={busy}
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void accept(file);
              e.target.value = "";
            }}
          />
        </label>
      )}

      {state.kind === "error" && (
        <p
          role="alert"
          className="rounded-2xl bg-red/15 p-4 font-body text-base text-paper"
        >
          {state.message}
        </p>
      )}

      <p className="font-body text-base text-muted-dark">
        Have text instead of a file?{" "}
        <Link
          href="/reading/new"
          className="font-semibold text-blue underline underline-offset-4 hover:opacity-80"
        >
          Paste it instead
        </Link>
        . Already have an account?{" "}
        <Link
          href="/sign-in"
          className="font-semibold text-blue underline underline-offset-4 hover:opacity-80"
        >
          Sign in
        </Link>
        .
      </p>

      {state.kind === "saved" && dialogOpen && (
        <AuthDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          next={`/start?pending=${state.id}`}
          fileName={state.name}
          googleEnabled={googleEnabled}
          mailEnabled={mailEnabled}
        />
      )}
    </div>
  );
}
